// w45 Cheksiz osmon (Endless Sky) — catalog preview card.
//
// The full template is one continuous scrolling sky that blends from
// daylight down to a starry night. The catalog card compresses that
// journey into one still frame: a vertical day-to-night gradient backdrop
// (pale sky blue at the top fading through dusk to deep night at the
// base), a few soft cloud blobs drifting in the upper daylight band, tiny
// star dots scattered in the lower night band, a small gold sun dot near
// the top, and a frosted glass rounded card holding the couple's names and
// a date rule. Uses the site's already-loaded serif stack (via `font`) for
// the names only — no new font import.
import { type LayoutCtx } from "./_shared";

// Deterministic pseudo-random helper so the card renders identically on
// every paint (no client/server mismatch, no re-shuffle on re-render).
function seeded(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

const CLOUD_SEEDS = [
  { top: 10, left: 14, w: 34, h: 10, o: 0.8 },
  { top: 18, left: 58, w: 26, h: 8, o: 0.6 },
  { top: 8, left: 70, w: 20, h: 7, o: 0.5 },
];

const STAR_COUNT = 16;

export function OsmonPreviewLayout({ names, date, accent, font }: LayoutCtx) {
  const gold = accent || "#3d6a99";
  const glass = "rgba(255,255,255,0.28)";

  const stars = Array.from({ length: STAR_COUNT }, (_, i) => {
    const left = seeded(i * 3.1 + 1) * 100;
    const top = 58 + seeded(i * 7.7 + 2) * 40; // confine to the lower/night band
    const size = 1 + seeded(i * 5.3 + 3) * 1.4;
    const opacity = 0.4 + seeded(i * 2.2 + 4) * 0.6;
    return { left, top, size, opacity };
  });

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // Day (pale sky) → dusk → night, top to bottom — the compressed
        // full-page sky journey.
        background:
          "linear-gradient(180deg, #cbecfe 0%, #8fc7e8 22%, #4f7fb0 46%, #2c4d78 68%, #16233f 88%, #0b1224 100%)",
        overflow: "hidden",
      }}
    >
      {/* Small gold sun dot near the top of the daylight band. */}
      <div
        style={{
          position: "absolute",
          top: "8%",
          right: "16%",
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: "#ffd873",
          boxShadow: "0 0 10px 3px rgba(255,216,115,0.55)",
        }}
      />

      {/* Soft cloud blobs drifting in the daylight band. */}
      {CLOUD_SEEDS.map((c, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: `${c.top}%`,
            left: `${c.left}%`,
            width: c.w,
            height: c.h,
            borderRadius: 999,
            background: "rgba(255,255,255,0.85)",
            opacity: c.o,
            filter: "blur(0.5px)",
          }}
        />
      ))}

      {/* Tiny star dots scattered through the night band. */}
      {stars.map((s, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: "#ffffff",
            opacity: s.opacity,
          }}
        />
      ))}

      {/* Frosted glass card carrying names + date. */}
      <div
        style={{
          position: "absolute",
          left: "10%",
          right: "10%",
          bottom: "12%",
          background: glass,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 10,
          padding: "10% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: font,
            fontSize: 14,
            lineHeight: 1.25,
            color: "#ffffff",
            textShadow: "0 1px 4px rgba(0,0,0,0.35)",
            marginBottom: "8%",
          }}
        >
          {names}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "8%" }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: gold }} />
          <div style={{ width: 34, height: 1, background: "rgba(255,255,255,0.55)" }} />
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: gold }} />
        </div>

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.26em",
            color: "#ffffff",
          }}
        >
          {date.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
